import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import FocusTrap from '../../design-system/primitives/FocusTrap';
import {
  MODAL_BASE_Z,
  isTopModalLayer,
  lockBodyScroll,
  popModalLayer,
  pushModalLayer,
  unlockBodyScroll,
} from '../../lib/modalStack';

/**
 * AppModal — unified modal with ESC + click-outside + X button + focus trap.
 *
 * Props:
 *   open             boolean
 *   onClose          () => void
 *   title            string (optional)
 *   children         ReactNode
 *   size             'sm' | 'md' | 'lg' | 'xl' | 'full' (default 'md')
 *   dismissible      boolean — allow ESC + backdrop click (default true)
 *   showCloseButton  boolean (default true)
 *   className        extra classes for the content box
 *   contentClassName extra classes for the inner body padding wrapper
 *   footer           optional ReactNode rendered in a sticky footer
 */
const SIZE_CLASSES = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[95vw]',
};

export default function AppModal({
  open,
  onClose,
  title,
  children,
  size = 'md',
  dismissible = true,
  showCloseButton = true,
  className = '',
  contentClassName = '',
  footer = null,
}) {
  // MFG-PR-3 — every open modal registers in the shared stack: it gets an
  // ascending z-index (newest always on top) and a ref-counted body scroll
  // lock that survives non-LIFO closes of stacked modals.
  const [layer, setLayer] = useState(null);
  const layerRef = useRef(null);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const newLayer = pushModalLayer();
    layerRef.current = newLayer;
    setLayer(newLayer);
    lockBodyScroll();
    return () => {
      popModalLayer(newLayer.id);
      unlockBodyScroll();
      layerRef.current = null;
      setLayer(null);
    };
  }, [open]);

  if (!open) return null;

  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && dismissible) onClose();
  };

  // ESC must close only the TOPMOST modal: FocusTrap listens on document in
  // the capture phase, so without this guard the oldest listener wins and
  // closes the bottom of the stack.
  const handleEscape = () => {
    const current = layerRef.current;
    if (dismissible && current && isTopModalLayer(current.id)) onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in-0 duration-150"
      style={{ zIndex: layer?.zIndex ?? MODAL_BASE_Z }}
      onClick={handleBackdropClick}
      role="presentation"
      dir="rtl"
    >
      <FocusTrap active onEscape={handleEscape}>
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'app-modal-title' : undefined}
          className={cn(
            'bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-h-[90vh] flex flex-col overflow-hidden',
            sizeClass,
            className
          )}
        >
          {(title || showCloseButton) && (
            <div className="flex items-center justify-between gap-3 p-5 border-b border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-t-2xl flex-shrink-0">
              {title ? (
                <h2
                  id="app-modal-title"
                  className="flex-1 min-w-0 break-words text-lg font-bold text-slate-800 dark:text-slate-100"
                >
                  {title}
                </h2>
              ) : <span />}
              {showCloseButton && (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="إغلاق"
                  className="flex-shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 dark:hover:text-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-400"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          )}

          <div className={cn('p-5 overflow-y-auto flex-1', contentClassName)}>
            {children}
          </div>

          {footer && (
            <div className="px-5 py-4 border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 rounded-b-2xl flex-shrink-0">
              {footer}
            </div>
          )}
        </div>
      </FocusTrap>
    </div>,
    document.body
  );
}
